import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useTenant } from '@/contexts/TenantContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { API_URL } from '@/utils/api';
import { DownloadButton } from '@/utils/DownloadButton';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Separator } from '@/components/ui/separator';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, PieChart, Pie, Cell, Legend, AreaChart, Area
} from 'recharts';
import {
  Users, TrendingUp, TrendingDown, Clock, AlertTriangle, Timer, CheckCircle,
  Loader2, BarChart3, FileSpreadsheet, Download, ArrowUp, ArrowDown, Minus,
  Lightbulb, ChevronDown, ChevronUp, Eye, CalendarDays, CalendarRange
} from 'lucide-react';

const API = API_URL;

function formatDateFr(date) {
  if (!date) return '';
  const d = new Date(date);
  return d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' });
}

function toISODate(date) {
  if (!date) return null;
  const d = new Date(date);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/* ===== PERIOD SELECTOR COMPONENT ===== */
function PeriodSelector({ value, onChange, fr }) {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState('presets'); // presets | single | range
  const [singleDate, setSingleDate] = useState(null);
  const [rangeFrom, setRangeFrom] = useState(null);
  const [rangeTo, setRangeTo] = useState(null);

  const presets = [
    { id: 'today', label: fr ? "Aujourd'hui" : 'Today', getRange: () => { const d = new Date(); return { startDate: toISODate(d), endDate: toISODate(d) }; }},
    { id: 'yesterday', label: fr ? 'Hier' : 'Yesterday', getRange: () => { const d = new Date(); d.setDate(d.getDate() - 1); return { startDate: toISODate(d), endDate: toISODate(d) }; }},
    { id: '7', label: fr ? '7 jours' : '7 days', days: 7 },
    { id: '14', label: fr ? '14 jours' : '14 days', days: 14 },
    { id: '30', label: fr ? '30 jours' : '30 days', days: 30 },
  ];

  const handlePreset = (preset) => {
    if (preset.days) {
      onChange({ type: 'days', days: preset.days, label: preset.label });
    } else {
      const range = preset.getRange();
      onChange({ type: 'range', startDate: range.startDate, endDate: range.endDate, label: preset.label });
    }
    setOpen(false);
  };

  const handleSingleDate = () => {
    if (!singleDate) return;
    const isoDate = toISODate(singleDate);
    onChange({
      type: 'range',
      startDate: isoDate,
      endDate: isoDate,
      label: formatDateFr(singleDate)
    });
    setOpen(false);
  };

  const handleRange = () => {
    if (!rangeFrom || !rangeTo) return;
    onChange({
      type: 'range',
      startDate: toISODate(rangeFrom),
      endDate: toISODate(rangeTo),
      label: `${formatDateFr(rangeFrom)} — ${formatDateFr(rangeTo)}`
    });
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="h-8 gap-2 text-xs font-medium min-w-[140px] justify-start"
          data-testid="period-selector-trigger"
        >
          <CalendarDays size={14} className="text-primary flex-shrink-0" />
          <span className="truncate">{value.label}</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="end" data-testid="period-selector-popover">
        <div className="flex">
          {/* Left: mode selector */}
          <div className="w-40 border-r border-slate-200 dark:border-slate-700 p-2 space-y-1" data-testid="period-mode-selector">
            <p className="text-[10px] uppercase font-bold text-slate-400 px-2 mb-2 tracking-wider">{fr ? 'Raccourcis' : 'Shortcuts'}</p>
            {presets.map(p => (
              <button
                key={p.id}
                onClick={() => handlePreset(p)}
                className={`w-full text-left px-3 py-1.5 rounded-md text-xs font-medium transition-colors
                  ${(value.type === 'days' && value.days === p.days) || (value.type === 'range' && value.label === p.label)
                    ? 'bg-primary text-white'
                    : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800'
                  }`}
                data-testid={`period-preset-${p.id}`}
              >
                {p.label}
              </button>
            ))}
            <Separator className="my-2" />
            <p className="text-[10px] uppercase font-bold text-slate-400 px-2 mb-2 tracking-wider">{fr ? 'Personnalise' : 'Custom'}</p>
            <button
              onClick={() => setMode('single')}
              className={`w-full text-left px-3 py-1.5 rounded-md text-xs font-medium transition-colors flex items-center gap-1.5
                ${mode === 'single' ? 'bg-blue-50 dark:bg-blue-950/40 text-blue-700 dark:text-blue-300' : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800'}`}
              data-testid="period-mode-single"
            >
              <CalendarDays size={12} /> {fr ? 'Date precise' : 'Specific date'}
            </button>
            <button
              onClick={() => setMode('range')}
              className={`w-full text-left px-3 py-1.5 rounded-md text-xs font-medium transition-colors flex items-center gap-1.5
                ${mode === 'range' ? 'bg-blue-50 dark:bg-blue-950/40 text-blue-700 dark:text-blue-300' : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800'}`}
              data-testid="period-mode-range"
            >
              <CalendarRange size={12} /> {fr ? 'Plage personnalisee' : 'Custom range'}
            </button>
          </div>

          {/* Right: calendar area */}
          {mode === 'single' && (
            <div className="p-3" data-testid="period-single-calendar">
              <p className="text-xs font-semibold text-slate-700 dark:text-slate-300 mb-2 px-1">{fr ? 'Choisir une date' : 'Pick a date'}</p>
              <Calendar
                mode="single"
                selected={singleDate}
                onSelect={setSingleDate}
                disabled={(date) => date > new Date()}
                className="rounded-md border-0"
              />
              <div className="flex justify-end mt-2">
                <Button size="sm" disabled={!singleDate} onClick={handleSingleDate} className="text-xs h-7" data-testid="period-apply-single">
                  {fr ? 'Appliquer' : 'Apply'}
                </Button>
              </div>
            </div>
          )}

          {mode === 'range' && (
            <div className="p-3" data-testid="period-range-calendar">
              <p className="text-xs font-semibold text-slate-700 dark:text-slate-300 mb-2 px-1">
                {fr ? 'Date de debut' : 'Start date'}
                {rangeFrom && <span className="ml-2 text-primary font-normal">{formatDateFr(rangeFrom)}</span>}
              </p>
              <Calendar
                mode="single"
                selected={rangeFrom}
                onSelect={setRangeFrom}
                disabled={(date) => date > new Date() || (rangeTo && date > rangeTo)}
                className="rounded-md border-0"
              />
              <Separator className="my-2" />
              <p className="text-xs font-semibold text-slate-700 dark:text-slate-300 mb-2 px-1">
                {fr ? 'Date de fin' : 'End date'}
                {rangeTo && <span className="ml-2 text-primary font-normal">{formatDateFr(rangeTo)}</span>}
              </p>
              <Calendar
                mode="single"
                selected={rangeTo}
                onSelect={setRangeTo}
                disabled={(date) => date > new Date() || (rangeFrom && date < rangeFrom)}
                className="rounded-md border-0"
              />
              <div className="flex justify-end mt-2">
                <Button size="sm" disabled={!rangeFrom || !rangeTo} onClick={handleRange} className="text-xs h-7" data-testid="period-apply-range">
                  {fr ? 'Appliquer' : 'Apply'}
                </Button>
              </div>
            </div>
          )}

          {mode === 'presets' && (
            <div className="flex items-center justify-center p-8 text-xs text-slate-400 dark:text-slate-500 w-56">
              <div className="text-center">
                <CalendarDays size={24} className="mx-auto mb-2 opacity-40" />
                <p>{fr ? 'Choisissez un raccourci ou un mode personnalise' : 'Choose a shortcut or custom mode'}</p>
              </div>
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

export default function AnalyticsPage() {
  const { effectiveTenantId } = useTenant();
  const { token } = useAuth();
  const { language } = useLanguage();
  const fr = language === 'fr';

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState({ type: 'days', days: 14, label: fr ? '14 jours' : '14 days' });
  const [sortField, setSortField] = useState('totalWorked');
  const [sortDir, setSortDir] = useState('desc');
  const [insightsOpen, setInsightsOpen] = useState(true);

  const fetchData = useCallback(async () => {
    if (!effectiveTenantId) return;
    setLoading(true);
    try {
      let url = `${API}/analytics/overview?tenantId=${effectiveTenantId}`;
      if (period.type === 'range') {
        url += `&startDate=${period.startDate}&endDate=${period.endDate}`;
      } else {
        url += `&days=${period.days}`;
      }
      const res = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [effectiveTenantId, period, token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Build export URL params
  const exportParams = period.type === 'range'
    ? `startDate=${period.startDate}&endDate=${period.endDate}`
    : `days=${period.days}`;

  // Period display label for the header
  const periodDisplay = useMemo(() => {
    if (!data?.period) return period.label;
    const { startDate, endDate } = data.period;
    if (startDate === endDate) {
      return `${fr ? 'Analyse du' : 'Analysis for'} ${formatDateFr(startDate)}`;
    }
    return `${fr ? 'Analyse du' : 'Analysis from'} ${formatDateFr(startDate)} ${fr ? 'au' : 'to'} ${formatDateFr(endDate)}`;
  }, [data, fr, period.label]);

  // Computed insights
  const insights = useMemo(() => {
    if (!data) return [];
    const { summary, dailyTrend, employeeStats } = data;
    const items = [];
    const trend = dailyTrend || [];
    const half = Math.floor(trend.length / 2);
    const firstHalf = trend.slice(0, half);
    const secondHalf = trend.slice(half);

    // Attendance trend
    const avgFirst = firstHalf.length ? firstHalf.reduce((s, d) => s + d.presentCount, 0) / firstHalf.length : 0;
    const avgSecond = secondHalf.length ? secondHalf.reduce((s, d) => s + d.presentCount, 0) / secondHalf.length : 0;
    if (avgSecond > avgFirst * 1.05) {
      items.push({ type: 'positive', icon: TrendingUp, text: fr ? `Présence en hausse (+${Math.round((avgSecond - avgFirst) / Math.max(avgFirst, 1) * 100)}%)` : `Attendance trending up (+${Math.round((avgSecond - avgFirst) / Math.max(avgFirst, 1) * 100)}%)` });
    } else if (avgSecond < avgFirst * 0.95) {
      items.push({ type: 'warning', icon: TrendingDown, text: fr ? `Présence en baisse (${Math.round((avgSecond - avgFirst) / Math.max(avgFirst, 1) * 100)}%)` : `Attendance trending down (${Math.round((avgSecond - avgFirst) / Math.max(avgFirst, 1) * 100)}%)` });
    }

    // Late arrivals trend
    const lateFirst = firstHalf.length ? firstHalf.reduce((s, d) => s + d.lateCount, 0) : 0;
    const lateSecond = secondHalf.length ? secondHalf.reduce((s, d) => s + d.lateCount, 0) : 0;
    if (lateSecond > lateFirst * 1.2) {
      items.push({ type: 'danger', icon: AlertTriangle, text: fr ? `Retards en augmentation (${lateSecond} vs ${lateFirst} période préc.)` : `Late arrivals increasing (${lateSecond} vs ${lateFirst} prev. period)` });
    } else if (lateSecond < lateFirst * 0.8) {
      items.push({ type: 'positive', icon: CheckCircle, text: fr ? `Retards en diminution (${lateSecond} vs ${lateFirst} période préc.)` : `Late arrivals decreasing (${lateSecond} vs ${lateFirst} prev. period)` });
    }

    // Top late employee
    const topLate = [...(employeeStats || [])].sort((a, b) => b.lateCount - a.lateCount)[0];
    if (topLate && topLate.lateCount > 3) {
      items.push({ type: 'warning', icon: AlertTriangle, text: fr ? `${topLate.name} cumule ${topLate.lateCount} retards` : `${topLate.name} has ${topLate.lateCount} late arrivals` });
    }

    // Overtime alert
    if (summary.totalOvertimeMinutes > 0) {
      const avgOT = Math.round(summary.totalOvertimeMinutes / 60 / Math.max(trend.length, 1) * 10) / 10;
      items.push({ type: avgOT > 2 ? 'danger' : 'info', icon: Timer, text: fr ? `Moyenne ${avgOT}h supplémentaires/jour` : `Average ${avgOT}h overtime/day` });
    }

    // High attendance
    if (summary.attendanceRate >= 95) {
      items.push({ type: 'positive', icon: CheckCircle, text: fr ? `Excellent taux de présence (${summary.attendanceRate}%)` : `Excellent attendance rate (${summary.attendanceRate}%)` });
    }

    return items;
  }, [data, fr]);

  // Period comparison
  const comparison = useMemo(() => {
    if (!data?.dailyTrend) return null;
    const trend = data.dailyTrend;
    const half = Math.floor(trend.length / 2);
    const prev = trend.slice(0, half);
    const curr = trend.slice(half);
    if (!prev.length || !curr.length) return null;

    const avgPresencePrev = prev.reduce((s, d) => s + d.presentCount, 0) / prev.length;
    const avgPresenceCurr = curr.reduce((s, d) => s + d.presentCount, 0) / curr.length;
    const totalLatePrev = prev.reduce((s, d) => s + d.lateCount, 0);
    const totalLateCurr = curr.reduce((s, d) => s + d.lateCount, 0);
    const totalHoursPrev = prev.reduce((s, d) => s + d.totalWorked, 0) / 60;
    const totalHoursCurr = curr.reduce((s, d) => s + d.totalWorked, 0) / 60;
    const totalOTPrev = prev.reduce((s, d) => s + d.overtimeMinutes, 0) / 60;
    const totalOTCurr = curr.reduce((s, d) => s + d.overtimeMinutes, 0) / 60;

    return {
      presence: { prev: Math.round(avgPresencePrev * 10) / 10, curr: Math.round(avgPresenceCurr * 10) / 10, delta: Math.round((avgPresenceCurr - avgPresencePrev) / Math.max(avgPresencePrev, 1) * 100) },
      late: { prev: totalLatePrev, curr: totalLateCurr, delta: totalLatePrev ? Math.round((totalLateCurr - totalLatePrev) / totalLatePrev * 100) : 0 },
      hours: { prev: Math.round(totalHoursPrev * 10) / 10, curr: Math.round(totalHoursCurr * 10) / 10, delta: totalHoursPrev ? Math.round((totalHoursCurr - totalHoursPrev) / totalHoursPrev * 100) : 0 },
      overtime: { prev: Math.round(totalOTPrev * 10) / 10, curr: Math.round(totalOTCurr * 10) / 10, delta: totalOTPrev ? Math.round((totalOTCurr - totalOTPrev) / totalOTPrev * 100) : 0 },
    };
  }, [data]);

  // Sorted employees
  const sortedEmployees = useMemo(() => {
    if (!data?.employeeStats) return [];
    return [...data.employeeStats].sort((a, b) => {
      const valA = sortField === 'name' ? a.name : a[sortField] || 0;
      const valB = sortField === 'name' ? b.name : b[sortField] || 0;
      if (sortField === 'name') return sortDir === 'asc' ? valA.localeCompare(valB) : valB.localeCompare(valA);
      return sortDir === 'asc' ? valA - valB : valB - valA;
    });
  }, [data, sortField, sortDir]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('desc');
    }
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <ChevronDown size={12} className="text-slate-300" />;
    return sortDir === 'asc' ? <ChevronUp size={12} className="text-primary" /> : <ChevronDown size={12} className="text-primary" />;
  };

  const DeltaBadge = ({ delta, invert = false }) => {
    const positive = invert ? delta < 0 : delta > 0;
    const negative = invert ? delta > 0 : delta < 0;
    if (delta === 0) return <span className="text-[10px] text-slate-400 flex items-center gap-0.5"><Minus size={10} /> 0%</span>;
    return (
      <span className={`text-[10px] flex items-center gap-0.5 font-medium ${positive ? 'text-green-600 dark:text-green-400' : negative ? 'text-red-600 dark:text-red-400' : 'text-slate-400'}`}>
        {delta > 0 ? <ArrowUp size={10} /> : <ArrowDown size={10} />}
        {Math.abs(delta)}%
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="animate-spin text-primary" size={32} />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="text-center py-12 text-slate-500 dark:text-slate-400">
        {fr ? 'Aucune donnée disponible' : 'No data available'}
      </div>
    );
  }

  const { summary, dailyTrend, employeeStats, departmentStats } = data;

  const trendData = dailyTrend.map(d => ({
    date: d.date.slice(5),
    fullDate: d.date,
    heures: Math.round(d.totalWorked / 60 * 10) / 10,
    retards: d.lateCount,
    presents: d.presentCount,
    absents: d.absentCount,
    overtime: Math.round(d.overtimeMinutes / 60 * 10) / 10
  }));

  const deptData = departmentStats.map(d => ({
    name: d.department,
    heures: Math.round(d.totalWorked / 60 * 10) / 10,
    overtime: Math.round(d.overtimeMinutes / 60 * 10) / 10,
    retards: d.lateCount,
    employes: d.employeeCount
  }));

  const total = summary.totalEmployees || 1;
  const presencePieData = [
    { name: fr ? 'Présents' : 'Present', value: summary.presentToday, pct: Math.round(summary.presentToday / total * 100), color: '#10b981' },
    { name: fr ? 'En pause' : 'On Break', value: summary.onBreak, pct: Math.round(summary.onBreak / total * 100), color: '#f59e0b' },
    { name: fr ? 'Absents' : 'Absent', value: Math.max(0, total - summary.presentToday - summary.onBreak), pct: Math.round(Math.max(0, total - summary.presentToday - summary.onBreak) / total * 100), color: '#ef4444' }
  ].filter(d => d.value > 0);

  const ChartTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white dark:bg-slate-800 p-3 rounded-lg shadow-lg border dark:border-slate-700 text-sm">
          <p className="font-semibold text-slate-800 dark:text-white mb-1">{label}</p>
          {payload.map((entry, i) => (
            <p key={i} style={{ color: entry.color }} className="text-xs flex items-center gap-1">
              <span className="w-2 h-2 rounded-full inline-block" style={{ background: entry.color }} />
              {entry.name}: <strong>{entry.value}</strong>
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  const PieLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, value, pct }) => {
    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);
    if (value === 0) return null;
    return (
      <text x={x} y={y} fill="white" textAnchor="middle" dominantBaseline="central" fontSize={12} fontWeight="bold">
        {value}
      </text>
    );
  };

  return (
    <div className="space-y-4" data-testid="analytics-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="font-heading text-2xl font-bold text-slate-900 dark:text-white">{fr ? 'Analytique' : 'Analytics'}</h1>
          </div>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5" data-testid="period-display">{periodDisplay}</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <PeriodSelector value={period} onChange={setPeriod} fr={fr} />
          <DownloadButton url={`${API}/analytics/export?tenantId=${effectiveTenantId}&${exportParams}&format=excel`} className="gap-1 h-8" size="sm" data-testid="analytics-export-excel-btn">
            <FileSpreadsheet size={14} /> Excel
          </DownloadButton>
          <DownloadButton url={`${API}/analytics/export?tenantId=${effectiveTenantId}&${exportParams}&format=pdf`} className="gap-1 h-8" size="sm" data-testid="analytics-export-pdf-btn">
            <Download size={14} /> PDF
          </DownloadButton>
        </div>
      </div>

      {/* Executive Summary — 4 KPIs with period comparison */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="executive-summary">
        <Card className="border-2 border-green-200 dark:border-green-800 bg-gradient-to-br from-green-50 dark:from-green-950/30 to-white dark:to-slate-900" data-testid="kpi-attendance">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-1">
              <p className="text-xs font-semibold text-green-600 dark:text-green-400 uppercase tracking-wide">{fr ? '% Présence' : 'Attendance %'}</p>
              <div className="p-1.5 rounded-lg bg-green-100 dark:bg-green-900/40"><CheckCircle size={16} className="text-green-600 dark:text-green-400" /></div>
            </div>
            <p className="text-3xl font-black text-green-700 dark:text-green-300">{summary.attendanceRate}%</p>
            {comparison && <DeltaBadge delta={comparison.presence.delta} />}
          </CardContent>
        </Card>

        <Card className="border-2 border-amber-200 dark:border-amber-800 bg-gradient-to-br from-amber-50 dark:from-amber-950/30 to-white dark:to-slate-900" data-testid="kpi-late">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-1">
              <p className="text-xs font-semibold text-amber-600 dark:text-amber-400 uppercase tracking-wide">{fr ? 'Retards' : 'Late'}</p>
              <div className="p-1.5 rounded-lg bg-amber-100 dark:bg-amber-900/40"><AlertTriangle size={16} className="text-amber-600 dark:text-amber-400" /></div>
            </div>
            <p className="text-3xl font-black text-amber-700 dark:text-amber-300">{summary.totalLateIncidents}</p>
            {comparison && <DeltaBadge delta={comparison.late.delta} invert />}
          </CardContent>
        </Card>

        <Card className="border-2 border-blue-200 dark:border-blue-800 bg-gradient-to-br from-blue-50 dark:from-blue-950/30 to-white dark:to-slate-900" data-testid="kpi-hours">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-1">
              <p className="text-xs font-semibold text-blue-600 dark:text-blue-400 uppercase tracking-wide">{fr ? 'Heures totales' : 'Total Hours'}</p>
              <div className="p-1.5 rounded-lg bg-blue-100 dark:bg-blue-900/40"><Clock size={16} className="text-blue-600 dark:text-blue-400" /></div>
            </div>
            <p className="text-3xl font-black text-blue-700 dark:text-blue-300">{comparison ? comparison.hours.curr : Math.round(trendData.reduce((s, d) => s + d.heures, 0) * 10) / 10}h</p>
            {comparison && <DeltaBadge delta={comparison.hours.delta} />}
          </CardContent>
        </Card>

        <Card className="border-2 border-purple-200 dark:border-purple-800 bg-gradient-to-br from-purple-50 dark:from-purple-950/30 to-white dark:to-slate-900" data-testid="kpi-overtime">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-1">
              <p className="text-xs font-semibold text-purple-600 dark:text-purple-400 uppercase tracking-wide">{fr ? 'Heures sup.' : 'Overtime'}</p>
              <div className="p-1.5 rounded-lg bg-purple-100 dark:bg-purple-900/40"><Timer size={16} className="text-purple-600 dark:text-purple-400" /></div>
            </div>
            <p className="text-3xl font-black text-purple-700 dark:text-purple-300">{Math.round(summary.totalOvertimeMinutes / 60 * 10) / 10}h</p>
            {comparison && <DeltaBadge delta={comparison.overtime.delta} invert />}
          </CardContent>
        </Card>
      </div>

      {/* Insights — auto-generated */}
      {insights.length > 0 && (
        <Card className="border-2 border-indigo-200 dark:border-indigo-800" data-testid="insights-section">
          <CardContent className="p-3">
            <button onClick={() => setInsightsOpen(!insightsOpen)} className="flex items-center gap-2 w-full text-left mb-2" data-testid="insights-toggle">
              <Lightbulb size={16} className="text-indigo-500" />
              <span className="text-sm font-bold text-slate-800 dark:text-white">{fr ? 'Insights automatiques' : 'Auto Insights'}</span>
              <Badge className="bg-indigo-100 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300 text-[10px]">{insights.length}</Badge>
              {insightsOpen ? <ChevronUp size={14} className="ml-auto text-slate-400" /> : <ChevronDown size={14} className="ml-auto text-slate-400" />}
            </button>
            {insightsOpen && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-1.5">
                {insights.map((item, i) => (
                  <div key={i} className={`flex items-center gap-2 p-2 rounded-md text-xs ${
                    item.type === 'positive' ? 'bg-green-50 dark:bg-green-950/20 text-green-700 dark:text-green-400' :
                    item.type === 'warning' ? 'bg-amber-50 dark:bg-amber-950/20 text-amber-700 dark:text-amber-400' :
                    item.type === 'danger' ? 'bg-red-50 dark:bg-red-950/20 text-red-700 dark:text-red-400' :
                    'bg-blue-50 dark:bg-blue-950/20 text-blue-700 dark:text-blue-400'
                  }`} data-testid={`insight-${i}`}>
                    <item.icon size={14} className="shrink-0" />
                    <span className="font-medium">{item.text}</span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Charts Row 1: Presence Area + Donut */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-2" data-testid="chart-presence-trend">
          <CardHeader className="pb-2 pt-3 px-4">
            <CardTitle className="text-sm font-bold text-slate-800 dark:text-white flex items-center gap-2">
              <TrendingUp size={16} className="text-blue-500" />
              {fr ? 'Tendance présence & retards' : 'Attendance & Late Trend'}
            </CardTitle>
          </CardHeader>
          <CardContent className="px-2 pb-3">
            <ResponsiveContainer width="100%" height={240}>
              <AreaChart data={trendData}>
                <defs>
                  <linearGradient id="gradPresent" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10b981" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="gradAbsent" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#ef4444" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#ef4444" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} stroke="#94a3b8" />
                <YAxis tick={{ fontSize: 11 }} stroke="#94a3b8" />
                <Tooltip content={<ChartTooltip />} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Area type="monotone" dataKey="presents" name={fr ? 'Présents' : 'Present'} stroke="#10b981" fill="url(#gradPresent)" strokeWidth={2} />
                <Area type="monotone" dataKey="absents" name={fr ? 'Absents' : 'Absent'} stroke="#ef4444" fill="url(#gradAbsent)" strokeWidth={2} />
                <Line type="monotone" dataKey="retards" name={fr ? 'Retards' : 'Late'} stroke="#f59e0b" strokeWidth={2} dot={{ r: 3 }} strokeDasharray="5 5" />
              </AreaChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Donut with values + % + legend */}
        <Card data-testid="chart-presence-donut">
          <CardHeader className="pb-1 pt-3 px-4">
            <CardTitle className="text-sm font-bold text-slate-800 dark:text-white flex items-center gap-2">
              <Users size={16} className="text-green-500" />
              {fr ? "Répartition aujourd'hui" : "Today's Split"}
            </CardTitle>
          </CardHeader>
          <CardContent className="pb-3 px-2">
            <ResponsiveContainer width="100%" height={160}>
              <PieChart>
                <Pie data={presencePieData} cx="50%" cy="50%" innerRadius={45} outerRadius={70} paddingAngle={3} dataKey="value" label={PieLabel} labelLine={false}>
                  {presencePieData.map((entry, i) => (
                    <Cell key={i} fill={entry.color} stroke="none" />
                  ))}
                </Pie>
                <Tooltip content={({ active, payload }) => {
                  if (active && payload?.[0]) {
                    const d = payload[0].payload;
                    return (
                      <div className="bg-white dark:bg-slate-800 p-2 rounded-lg shadow-lg border dark:border-slate-700 text-xs">
                        <p className="font-bold" style={{ color: d.color }}>{d.name}</p>
                        <p>{d.value} ({d.pct}%)</p>
                      </div>
                    );
                  }
                  return null;
                }} />
              </PieChart>
            </ResponsiveContainer>
            <div className="flex flex-col gap-1 px-2">
              {presencePieData.map((d, i) => (
                <div key={i} className="flex items-center justify-between text-xs">
                  <span className="flex items-center gap-1.5">
                    <span className="w-2.5 h-2.5 rounded-full" style={{ background: d.color }} />
                    <span className="text-slate-600 dark:text-slate-400">{d.name}</span>
                  </span>
                  <span className="font-bold text-slate-800 dark:text-slate-200">{d.value} <span className="font-normal text-slate-400">({d.pct}%)</span></span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row 2: Hours Bar + Department */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card data-testid="chart-hours-trend">
          <CardHeader className="pb-2 pt-3 px-4">
            <CardTitle className="text-sm font-bold text-slate-800 dark:text-white flex items-center gap-2">
              <BarChart3 size={16} className="text-blue-500" />
              {fr ? 'Heures travaillées & supplémentaires' : 'Worked & Overtime Hours'}
            </CardTitle>
          </CardHeader>
          <CardContent className="px-2 pb-3">
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={trendData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} stroke="#94a3b8" />
                <YAxis tick={{ fontSize: 11 }} stroke="#94a3b8" unit="h" />
                <Tooltip content={<ChartTooltip />} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="heures" name={fr ? 'Heures' : 'Hours'} fill="#3b82f6" radius={[3, 3, 0, 0]} />
                <Bar dataKey="overtime" name={fr ? 'Heures sup.' : 'Overtime'} fill="#8b5cf6" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card data-testid="chart-dept-overtime">
          <CardHeader className="pb-2 pt-3 px-4">
            <CardTitle className="text-sm font-bold text-slate-800 dark:text-white flex items-center gap-2">
              <Clock size={16} className="text-purple-500" />
              {fr ? 'Par département' : 'By Department'}
            </CardTitle>
          </CardHeader>
          <CardContent className="px-2 pb-3">
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={deptData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis type="number" tick={{ fontSize: 11 }} stroke="#94a3b8" unit="h" />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 10 }} stroke="#94a3b8" width={80} />
                <Tooltip content={<ChartTooltip />} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="heures" name={fr ? 'Heures' : 'Hours'} fill="#3b82f6" radius={[0, 3, 3, 0]} />
                <Bar dataKey="overtime" name={fr ? 'H. sup.' : 'OT'} fill="#8b5cf6" radius={[0, 3, 3, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Period Comparison */}
      {comparison && (
        <Card data-testid="period-comparison">
          <CardHeader className="pb-2 pt-3 px-4">
            <CardTitle className="text-sm font-bold text-slate-800 dark:text-white flex items-center gap-2">
              <BarChart3 size={16} className="text-indigo-500" />
              {fr ? 'Comparaison période actuelle vs précédente' : 'Current vs Previous Period'}
            </CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-3">
            <div className="grid grid-cols-4 gap-3">
              {[
                { label: fr ? 'Présence moy.' : 'Avg Attendance', prev: comparison.presence.prev, curr: comparison.presence.curr, delta: comparison.presence.delta, unit: '', positive: true },
                { label: fr ? 'Retards' : 'Late', prev: comparison.late.prev, curr: comparison.late.curr, delta: comparison.late.delta, unit: '', positive: false },
                { label: fr ? 'Heures' : 'Hours', prev: `${comparison.hours.prev}h`, curr: `${comparison.hours.curr}h`, delta: comparison.hours.delta, unit: '', positive: true },
                { label: fr ? 'Heures sup.' : 'Overtime', prev: `${comparison.overtime.prev}h`, curr: `${comparison.overtime.curr}h`, delta: comparison.overtime.delta, unit: '', positive: false },
              ].map((item, i) => (
                <div key={i} className="p-3 rounded-lg bg-slate-50 dark:bg-slate-800/40 border dark:border-slate-700" data-testid={`comparison-${i}`}>
                  <p className="text-[10px] text-slate-500 dark:text-slate-400 uppercase font-semibold tracking-wide mb-1">{item.label}</p>
                  <div className="flex items-end gap-2">
                    <span className="text-lg font-bold text-slate-800 dark:text-white">{item.curr}</span>
                    <DeltaBadge delta={item.delta} invert={!item.positive} />
                  </div>
                  <p className="text-[10px] text-slate-400 dark:text-slate-500 mt-0.5">{fr ? 'Préc.' : 'Prev.'}: {item.prev}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Employee Stats Table — sortable */}
      <Card data-testid="table-employee-stats">
        <CardHeader className="pb-2 pt-3 px-4 border-b bg-slate-50/50 dark:bg-slate-800/50">
          <CardTitle className="text-sm font-bold text-slate-800 dark:text-white flex items-center gap-2">
            <Users size={16} className="text-blue-500" />
            {fr ? 'Statistiques par employé' : 'Employee Statistics'}
            <Badge variant="outline" className="text-[10px] ml-1">{sortedEmployees.length}</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-100/80 dark:bg-slate-800/80">
                  <TableHead className="py-2">
                    <button onClick={() => handleSort('name')} className="flex items-center gap-1 text-xs font-bold" data-testid="sort-name">
                      {fr ? 'Employé' : 'Employee'} <SortIcon field="name" />
                    </button>
                  </TableHead>
                  <TableHead className="py-2 text-right">
                    <button onClick={() => handleSort('daysWorked')} className="flex items-center gap-1 text-xs font-bold ml-auto" data-testid="sort-days">
                      {fr ? 'Jours' : 'Days'} <SortIcon field="daysWorked" />
                    </button>
                  </TableHead>
                  <TableHead className="py-2 text-right">
                    <button onClick={() => handleSort('totalWorked')} className="flex items-center gap-1 text-xs font-bold ml-auto" data-testid="sort-hours">
                      {fr ? 'Heures' : 'Hours'} <SortIcon field="totalWorked" />
                    </button>
                  </TableHead>
                  <TableHead className="py-2 text-right">
                    <button onClick={() => handleSort('overtimeMinutes')} className="flex items-center gap-1 text-xs font-bold ml-auto" data-testid="sort-overtime">
                      {fr ? 'H. sup.' : 'OT'} <SortIcon field="overtimeMinutes" />
                    </button>
                  </TableHead>
                  <TableHead className="py-2 text-right">
                    <button onClick={() => handleSort('lateCount')} className="flex items-center gap-1 text-xs font-bold ml-auto" data-testid="sort-late">
                      {fr ? 'Retards' : 'Late'} <SortIcon field="lateCount" />
                    </button>
                  </TableHead>
                  <TableHead className="py-2 text-right text-xs font-bold">{fr ? 'Perf.' : 'Perf.'}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedEmployees.map((emp) => {
                  const hoursPerDay = emp.daysWorked > 0 ? Math.round(emp.totalWorked / 60 / emp.daysWorked * 10) / 10 : 0;
                  const perfColor = hoursPerDay >= 7.5 ? 'text-green-600 bg-green-50 dark:bg-green-950/30' : hoursPerDay >= 6 ? 'text-amber-600 bg-amber-50 dark:bg-amber-950/30' : 'text-red-600 bg-red-50 dark:bg-red-950/30';
                  return (
                    <TableRow key={emp.employeeId} className="hover:bg-slate-50 dark:hover:bg-slate-800/40 transition-colors" data-testid={`emp-row-${emp.code}`}>
                      <TableCell className="py-2">
                        <div>
                          <p className="font-medium text-sm text-slate-900 dark:text-slate-100">{emp.name}</p>
                          <p className="text-[10px] text-slate-400 dark:text-slate-500 font-mono">{emp.code}</p>
                        </div>
                      </TableCell>
                      <TableCell className="py-2 text-right text-sm text-slate-700 dark:text-slate-300">{emp.daysWorked}</TableCell>
                      <TableCell className="py-2 text-right text-sm font-bold text-slate-800 dark:text-slate-200">{Math.round(emp.totalWorked / 60 * 10) / 10}h</TableCell>
                      <TableCell className="py-2 text-right">
                        {emp.overtimeMinutes > 0 ? (
                          <span className="text-sm font-medium text-purple-600 dark:text-purple-400">{Math.round(emp.overtimeMinutes / 60 * 10) / 10}h</span>
                        ) : <span className="text-slate-300 dark:text-slate-600">-</span>}
                      </TableCell>
                      <TableCell className="py-2 text-right">
                        {emp.lateCount > 0 ? (
                          <Badge className="bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-400 text-[10px]">{emp.lateCount}</Badge>
                        ) : <span className="text-slate-300 dark:text-slate-600">-</span>}
                      </TableCell>
                      <TableCell className="py-2 text-right">
                        <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${perfColor}`}>{hoursPerDay}h/j</span>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
